/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
	Grid,
	TextField,
	Switch,
	Chip,
	Tooltip,
} from '@mui/material';
import {
	useDeleteAIHtmlTemplateMutation,
	useGetAIHtmlTemplatesQuery,
	useSaveAIHtmlTemplateMutation,
} from '@/features/templates/templatesApi';
import HtmlPreview from '@/components/HtmlPreview';
import { useDispatch, useSelector } from 'react-redux';
import { useGetBrokerGroupsQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import DOMPurify from 'dompurify';
import { openDialog } from '@/features/dialog/dialogSlice';
import { formatDate } from '@/utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import DevJSONView from '@/components/DevJSONView';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { showToast } from '@/features/toast/toastSlice';
import ShareDialog from '@/components/dialogs/ShareDialog';
import { NoRowsErrorOverlay, NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import { OwnerCellData } from '../OwnerCell';
import DataGridMultiBrokerGroup from '@/components/table/DataGridComponents/DataGridMultiBrokerGroup';

const parser = new DOMParser();

const EditHtml = ({ value, onChange, showEdit }) => {
	const [textValue, setTextValue] = useState(value);

	const validateHtml = (val) => {
		const sanitised = DOMPurify.sanitize(val);
		console.log('Sanitised', sanitised);
		const doc = parser.parseFromString(val, 'text/html');
		onChange(val);
	};

	useEffect(() => {
		setTextValue((prev) => (value !== prev ? value : prev));
	}, [value]);

	return (
		<Stack direction={'column'} height='100%'>
			<Box height={showEdit ? '50%' : '100%'}>
				<HtmlPreview showLabel={false} htmlValue={value} height={'100%'} />
			</Box>
			{showEdit && (
				<Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
					<TextField
						multiline
						fullWidth
						value={textValue}
						onChange={(e) => setTextValue(e.target.value)}
						onBlur={() => validateHtml(textValue)}
						sx={{
							height: '100%',
							display: 'flex',
							'& .MuiFormControl-root': {
								height: '100%',
							},
							'& .MuiInputBase-root': {
								height: '100%',
								alignItems: 'stretch',
								overflow: 'hidden',
							},
							'& .MuiInputBase-inputMultiline': {
								height: '100% !important',
								overflow: 'auto !important',
								boxSizing: 'border-box',
								WebkitOverflowScrolling: 'touch',
							},
							'& textarea': {
								height: '100% !important',
								overflow: 'auto !important',
								resize: 'none',
								boxSizing: 'border-box',
							},
						}}
					/>
				</Box>
			)}
		</Stack>
	);
};

const AIHtmlTemplatesTable = () => {
	const dispatch = useDispatch();
	const [rowSelectionModel, setRowSelectionModel] = useState([]);
	const [sortModel, setSortModel] = useState([
		{
			field: 'lastUpdated',
			sort: 'desc',
		},
	]);

	const [selectedRow, setSelectedRow] = useState(null);
	const [editRow, setEditRow] = useState(false);

	const [showDeleteDialog, setShowDeleteDialog] = useState(false);

	const showShareDialog = useSelector((state) => state.dialog.open?.share);

	const location = useLocation();
	const navigate = useNavigate();

	const isEditable = (row) => !row.master;

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const emailTemplatesQuery = useGetAIHtmlTemplatesQuery({
		showAdmin: userDetails?.isFolioAdmin,
	});

	const rows = (emailTemplatesQuery.data ?? []).map((r) => ({ ...r, canDelete: !r.master }));

	const [deleteTemplate, { isLoading: isDeleteLoading }] = useDeleteAIHtmlTemplateMutation();
	const [saveTemplate, { isLoading: isUpdateLoading }] = useSaveAIHtmlTemplateMutation();

	const handleSave = async ({ id, ...properties }) => {
		// console.log('Saving AI html template', id, properties);
		return await saveTemplate({
			docId: id,
			properties,
		})
			.unwrap()
			.then((res) => {
				console.log('update AI html template res', res);
				dispatch(
					showToast({
						message: 'Successfully updated AI html template',
						success: true,
					}),
				);
				return true;
			})
			.catch((e) => {
				console.error(`Error updating template ${id}`, e);
				dispatch(
					showToast({
						message: 'Error updating AI html template; please try again.',
						error: true,
					}),
				);
				return false;
			});
	};

	const handleDuplicate = async (row) => {
		console.log('Duplicating AI html template', row);
		return await saveTemplate({
			properties: { html: row.html, name: row.name ? `${row.name} Copy` : 'New Template' },
		})
			.unwrap()
			.then((res) => {
				console.log('update AI html template res', res);
				dispatch(
					showToast({
						message: 'Successfully created AI template html.',
						success: true,
					}),
				);
			})
			.catch((e) => {
				console.error(`Error creating template from ${row?.id}`, e);
				dispatch(
					showToast({
						message: 'Error creating AI template; please try again.',
						error: true,
					}),
				);
			});
	};

	const handleShare = async (row, userIds, brokerGroupIds) => {
		const newUserIds = Array.from(new Set([...userIds, userDetails?.userId]));

		return await saveTemplate({
			docId: row?.id,
			properties: { userIds: newUserIds, brokerGroupIds },
		})
			.unwrap()
			.then((res) => {
				console.log('update AI html template res', res);
				dispatch(
					showToast({
						message: 'Successfully updated AI template sharing settings.',
						success: true,
					}),
				);
			})
			.catch((e) => {
				console.error(`Error updating template ${row?.id}`, e);
				dispatch(
					showToast({
						message: 'Error updating AI template sharing settings; please try again.',
						error: true,
					}),
				);
			});
	};

	const handleDelete = async (row) => {
		const templateName = row?.name;
		console.log('Deleting AI html template', { row });
		await deleteTemplate({ docId: row?.id })
			.unwrap()
			.then((res) => {
				console.log('deleteTemplate res', res);
				dispatch(
					showToast({
						message: `Successfully deleted template "${templateName}".`,
						success: true,
					}),
				);
				setRowSelectionModel([]);
				setSelectedRow(null);
			})
			.catch((e) => {
				console.error(`Error deleting template ${row?.id}`, e);
				dispatch(
					showToast({
						message: `Error deleting template "${templateName}"; please try again.`,
						error: true,
					}),
				);
			});

		setShowDeleteDialog(false);
	};

	const isLoading = emailTemplatesQuery.isLoading || isDeleteLoading || isUpdateLoading;

	const columns = [
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
			minWidth: 200,
			editable: true,
			sortable: true,
			display: 'flex',
		},
		{
			field: 'ownerDocId',
			headerName: 'Created by',
			flex: 1,
			minWidth: 200,
			editable: false,
			sortable: true,
			display: 'flex',
			renderCell: (params) => {
				const { ownerDocId } = params.row;
				return ownerDocId ? (
					<OwnerCellData userId={ownerDocId} {...params} color='text.primary' />
				) : (
					<Typography color='text.primary'>{'Folio'}</Typography>
				);
			},
		},
		{
			field: 'active',
			headerName: 'Active',
			flex: 1,
			minWidth: 80,
			editable: true,
			sortable: true,
			display: 'flex',
			renderCell: (params) => {
				return (
					<Switch
						size='small'
						disabled={params.row?.master}
						checked={params.row?.active}
						onChange={async (e) => {
							const success = await handleSave({
								id: params.row.id,
								active: e.target.checked,
							});
						}}
						slotProps={{ input: { 'aria-label': 'controlled' } }}
					/>
				);
			},
		},
		{
			field: 'createdWhen',
			headerName: 'Date created',
			flex: 1,
			minWidth: 200,
			editable: false,
			sortable: true,
			display: 'flex',
			renderCell: (params) => {
				const { createdWhen } = params.row;
				return <Typography>{formatDate(createdWhen, true)}</Typography>;
			},
		},
		{
			field: 'lastUpdated',
			headerName: 'Last updated',
			flex: 1,
			minWidth: 200,
			editable: false,
			sortable: true,
			display: 'flex',
			renderCell: (params) => {
				const { lastUpdated } = params.row;
				return <Typography>{formatDate(lastUpdated, true)}</Typography>;
			},
		},
		{
			field: 'brokerGroupIds',
			headerName: 'Shared with',
			flex: 1,
			minWidth: 200,
			editable: false,
			sortable: false,
			display: 'flex',
			renderCell: (params) => {
				const { row } = params;
				return (
					<DataGridMultiBrokerGroup
						{...row}
						showAdmin={userDetails?.isFolioAdmin}
						onClick={() => {
							setSelectedRow(row);
							dispatch(
								openDialog({
									dialogName: 'share',
									data: { ...row },
								}),
							);
						}}
					/>
				);
			},
		},
	];

	const visible = Boolean(selectedRow?.id);

	return (
		<Box sx={{ minHeight: '50vh' }}>
			<Stack
				direction='column'
				spacing={1}
				sx={{ height: '100%' }}
				// onClick={() => {
				// 	if (!showDeleteDialog && !showShareDialog) {
				// 		setRowSelectionModel([]);
				// 		setSelectedRow(null);
				// 	}
				// }}
			>
				<Grid
					// size={{ sm: 12, md: 6 }}
					spacing={1}
					sx={{ px: '1em' }}
					container
					justifyContent={'flex-end'}
					alignItems={'baseline'}
				>
					{rowSelectionModel.length > 0 && (
						<>
							<Box>
								<Button
									variant='table_actions'
									disabled={selectedRow?.master || isLoading}
									onClick={async (e) => {
										e.stopPropagation();
										if (editRow) {
											await handleSave({
												id: selectedRow.id,
												html: selectedRow.html,
											});
										}
										setEditRow((prev) => !prev);
									}}
								>
									{editRow ? 'Save' : 'Edit'}
								</Button>
							</Box>
							<Box>
								<Button
									variant='table_actions'
									disabled={isLoading}
									onClick={(e) => {
										e.stopPropagation();
										handleDuplicate(selectedRow);
									}}
								>
									Duplicate
								</Button>
							</Box>
							<Box>
								<Button
									variant='table_actions'
									disabled={selectedRow.master || isLoading}
									onClick={(e) => {
										e.stopPropagation();
										console.log('Current template to share', selectedRow);
										dispatch(
											openDialog({
												dialogName: 'share',
												data: { ...selectedRow },
											}),
										);
									}}
								>
									Share
								</Button>
							</Box>
							<Box>
								<Button
									variant='table_actions'
									disabled={!selectedRow || !selectedRow.canDelete || isLoading}
									onClick={(e) => {
										e.stopPropagation();
										setShowDeleteDialog(true);
									}}
								>
									Delete
								</Button>
							</Box>
						</>
					)}
				</Grid>
				<Box
					sx={{
						display: { xs: 'block', sm: 'flex' },
						alignItems: 'stretch',
					}}
				>
					<Box
						sx={(theme) => ({
							width: { xs: '100%', sm: visible ? '50%' : '100%' },
							transition: theme.transitions.create('width', {
								duration: theme.transitions.duration.standard,
								easing: theme.transitions.easing.easeInOut,
							}),
							minWidth: 0,
						})}
					>
						<DataGrid
							sx={{
								'& .MuiDataGrid-editInputCell': {
									backgroundColor: 'transparent',
								},
								'--DataGrid-overlayHeight': '3em',
								// '--DataGrid-containerBackground': 'transparent',
								// '--DataGrid-pinnedBackground': 'transparent',
								'& .MuiDataGrid-cell': {
									padding: 1,
									minHeight: '42px',
								},
								border: 'none',
							}}
							loading={emailTemplatesQuery.isLoading}
							slotProps={{
								loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
							}}
							width='100%'
							getRowHeight={() => 'auto'}
							processRowUpdate={async (newRow, oldRow) => {
								console.log('processRowUpdate', { newRow, oldRow });
								const { html, ...update } = Object.entries(newRow).reduce(
									(acc, [key, value]) => {
										if (oldRow[key] !== value && value != null) {
											acc[key] = value;
										}
										return acc;
									},
									{ id: oldRow.id },
								);
								const success = await handleSave(update);
								return success ? newRow : oldRow;
							}}
							rows={rows}
							columns={columns}
							isCellEditable={(params) =>
								params.field === 'name' && isEditable(params.row)
							}
							onRowSelectionModelChange={(model) => {
								const rowId = model?.[0];
								setRowSelectionModel(model);
								setSelectedRow(rows.find((r) => r.id === rowId));
								setEditRow(false);
							}}
							initialState={{
								sorting: {
									sortModel,
								},
							}}
							sortingOrder={['desc', 'asc']}
							rowSelectionModel={rowSelectionModel}
							disableColumnResize
							disableColumnFilter
							disableColumnSelector
							disableColumnMenu
							onRowClick={(_, e) => {
								e.stopPropagation();
							}}
							hideFooter
							slots={{
								noRowsOverlay: emailTemplatesQuery.isError
									? () => <NoRowsErrorOverlay objectType={'templates'} />
									: () => <NoRowsOverlay objectType={'templates'} />,
							}}
							sortModel={sortModel}
							onSortModelChange={(newModel) => setSortModel(newModel)}
						/>
					</Box>
					<Box
						sx={(theme) => ({
							width: { xs: '100%', sm: visible ? '50%' : 0 },
							transition: theme.transitions.create('width', {
								duration: theme.transitions.duration.standard,
								easing: theme.transitions.easing.easeInOut,
							}),
							overflow: 'hidden',
							minWidth: 0,
							px: '1em',
							minHeight: '60vh',
						})}
					>
						<>
							{isLoading ? (
								<Box
									width='100%'
									display='flex'
									sx={{ justifyContent: 'center', mt: '1em', pt: '1em' }}
								>
									<CircularProgress />
								</Box>
							) : rowSelectionModel.length === 0 ? (
								<Box
									height='100%'
									width='100%'
									sx={{ backgroundColor: 'aliceblue' }}
								></Box>
							) : (
								<EditHtml
									value={selectedRow.html}
									onChange={(v) => {
										setSelectedRow((prev) => ({
											...prev,
											html: v,
										}));
									}}
									showEdit={editRow}
								/>
							)}
						</>
					</Box>
				</Box>
				<DevJSONView value={{ selectedRow, rowSelectionModel }} />
				{showDeleteDialog && (
					<ConfirmDialog
						openDialog={showDeleteDialog}
						title={'Delete AI Html Template'}
						handleClose={(e) => {
							e.stopPropagation();
							setShowDeleteDialog(false);
						}}
						handleConfirm={() => handleDelete(selectedRow)}
						content={
							<Stack spacing={1}>
								<Typography>
									{
										'Are you sure you want to delete this template? This cannot be undone.'
									}
								</Typography>
								<Typography>
									{
										'If you have shared this template with others, they will no longer be able to access it.'
									}
								</Typography>
							</Stack>
						}
					/>
				)}
				{showShareDialog && (
					<ShareDialog
						title={'Template'}
						hideUsers={true}
						showAdmin={userDetails?.isFolioAdmin}
						config={{ brokerGroup: { groupBy: 'ledger' } }}
						onConfirm={async (userIds, brokerGroupIds) => {
							if (selectedRow) {
								await handleShare(selectedRow, userIds, brokerGroupIds);
							}
						}}
					/>
				)}
			</Stack>
		</Box>
	);
};

export default AIHtmlTemplatesTable;
